import React, { useContext, useState, useEffect } from 'react';
import { ThemeContext } from '../../hooks/ThemeContext';
import { api } from '../../services/baseUrl';
import LoadingSpinner from '../globals/LoadingSpinner';
import { MessageSquare, User, Clock, XCircle, Mail } from 'lucide-react';
import toast from 'react-hot-toast';

const MessagesList = ({ onSelectConversation }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConversations();
    
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchConversations, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/conversations');
      if (response.data.success) {
        // Filtrer les conversations avec utilisateurs supprimés
        const validConversations = (response.data.conversations || []).filter(
          conv => conv.other_user && conv.other_user.id && 
                 (conv.other_user.firstname || conv.other_user.nickname)
        );
        setConversations(validConversations);
      }
      setError(null);
    } catch (err) {
      console.error('Erreur chargement conversations:', err);
      setError('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return d.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className={`p-4 rounded-lg ${isDark ? 'bg-red-500/10 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-200'}`}>
        {error}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className={`text-center py-12 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
        <MessageSquare className="mx-auto mb-4 opacity-30" size={48} />
        <h3 className="text-xl font-semibold mb-2">Aucune conversation</h3>
        <p className={isDark ? 'text-white/60' : 'text-gray-600'}>
          Contactez un créateur pour démarrer une conversation.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {conversations.map((conv) => (
        <div
          key={conv.conversation_id}
          onClick={() => onSelectConversation(conv)}
          className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border ${
            isDark 
              ? 'bg-[#0b0b0c] border-white/6 hover:border-white/20 hover:bg-white/5' 
              : 'bg-white border-gray-100 hover:border-gray-300 hover:shadow-md'
          }`}
        >
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
              isDark ? 'bg-gradient-to-br from-purple-500/20 to-blue-500/20' : 'bg-gradient-to-br from-purple-100 to-blue-100'
            }`}>
              <User size={24} className={isDark ? 'text-purple-300' : 'text-purple-600'} />
            </div>

            {/* Contenu */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-semibold truncate">
                  {conv.other_user?.firstname && conv.other_user?.lastname 
                    ? `${conv.other_user.firstname} ${conv.other_user.lastname}` 
                    : conv.other_user?.nickname || 'Utilisateur'}
                </h4>
                <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {formatDate(conv.last_message.created_at)}
                </span>
              </div>

              <p className={`text-sm truncate ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                {conv.last_message.is_mine && 'Vous: '}
                {conv.last_message.content}
              </p>

              <div className="flex items-center gap-2 mt-2">
                {/* Badge rouge pour messages non lus */}
                {conv.unread_count > 0 && (
                  <div className="flex items-center gap-1">
                    <Mail size={14} className="text-red-500" />
                    <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white">
                      {conv.unread_count}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MessagesList;
